import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, ArrowRight, Ticket, Leaf, Lightbulb, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [pinMode, setPinMode] = useState(null); // null | "standard" | "renalife"
  const [pinCode, setPinCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.phone, form.password);
      } else {
        await register(form.name, form.phone, form.password);
      }
      toast.success("¡Bienvenido líder!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const redeemPin = async (e) => {
    e.preventDefault();
    setRedeeming(true);
    try {
      await api.post("/pin/redeem", { code: pinCode, pin_type: pinMode });
      if (pinMode === "lumi") {
        toast.success("¡PIN LUMI válido! Bienvenido a LUMI.");
        window.location.href = "/dashboard/academia-lumi";
        return;
      }
      toast.success(pinMode === "renalife" ? "¡PIN RENALIFE válido! Bienvenido, distribuidor." : "¡PIN válido! Vamos a crear tu campaña.");
      navigate("/campana-pin", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-texture flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center text-2xl font-display font-black tracking-tight mb-8">
          MULTI<span className="text-purple-500">CONTACTOS</span>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 glow-purple">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="text-yellow-400" size={20} />
            <h1 className="font-display font-bold text-2xl">{mode === "login" ? "Inicia sesión" : "Crea tu cuenta"}</h1>
          </div>
          <p className="text-sm text-zinc-500 mb-6">Accede a tu módulo de líder de multinivel.</p>

          <Button
            data-testid="google-login-button"
            onClick={handleGoogle}
            variant="outline"
            className="w-full bg-white text-black hover:bg-zinc-200 font-semibold rounded-xl py-6 border-0"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continuar con Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-xs text-zinc-600 uppercase tracking-widest">o con tu celular</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nombre</Label>
                <Input
                  data-testid="name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Tu nombre"
                  required
                  className="mt-1.5 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Celular (10 dígitos)</Label>
              <Input
                data-testid="phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="5512345678"
                inputMode="numeric"
                required
                className="mt-1.5 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Contraseña</Label>
              <div className="relative">
                <Input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••"
                  required
                  className="mt-1.5 bg-black border-white/15 rounded-xl py-6 pr-12 focus-visible:ring-purple-500"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-500 hover:text-zinc-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              data-testid="submit-auth-button"
              type="submit"
              disabled={loading}
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl py-6"
            >
              {loading ? "Procesando..." : mode === "login" ? "Entrar" : "Crear cuenta"}
              {!loading && <ArrowRight size={18} className="ml-2" />}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
            <button
              data-testid="toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-purple-400 hover:text-purple-300 font-semibold"
            >
              {mode === "login" ? "Regístrate" : "Inicia sesión"}
            </button>
          </p>

          <div className="mt-6 pt-6 border-t border-white/10">
            {!pinMode ? (
              <div className="space-y-3">
                <button
                  data-testid="have-pin-button"
                  onClick={() => setPinMode("standard")}
                  className="w-full flex items-center justify-center gap-2 text-sm text-yellow-impact hover:text-yellow-300 font-semibold"
                >
                  <Ticket size={16} /> Tengo un PIN de campaña
                </button>
                <button
                  data-testid="have-renalife-pin-button"
                  onClick={() => setPinMode("renalife")}
                  className="w-full flex items-center justify-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 font-semibold"
                >
                  <Leaf size={16} /> Tengo un PIN de RENALIFE
                </button>
                <button
                  data-testid="have-lumi-pin-button"
                  onClick={() => setPinMode("lumi")}
                  className="w-full flex items-center justify-center gap-2 text-sm text-sky-400 hover:text-sky-300 font-semibold"
                >
                  <Lightbulb size={16} /> Tengo un PIN de LUMI
                </button>
              </div>
            ) : (() => {
              const PIN_UI = {
                standard: {
                  Icon: Ticket, iconCls: "text-yellow-400",
                  label: "Accede con tu PIN de campaña",
                  desc: "Crea una campaña completa gratis, sin registrarte. Escribe el PIN que te compartieron.",
                  inputRing: "focus-visible:ring-yellow-400",
                  buttonCls: "bg-yellow-400 hover:bg-yellow-300 text-black",
                  cta: "Acceder con PIN",
                },
                renalife: {
                  Icon: Leaf, iconCls: "text-emerald-400",
                  label: "Accede con tu PIN de RENALIFE",
                  desc: "Acceso exclusivo para distribuidores RENALIFE. Tus materiales (mensajes, imágenes, video y página) ya están listos con la marca.",
                  inputRing: "focus-visible:ring-emerald-500",
                  buttonCls: "bg-emerald-600 hover:bg-emerald-500 text-white",
                  cta: "Entrar como distribuidor RENALIFE",
                },
                lumi: {
                  Icon: Lightbulb, iconCls: "text-sky-400",
                  label: "Accede con tu PIN de LUMI",
                  desc: "Acceso exclusivo para distribuidores LUMI: tutoría educativa para niños de 6 a 14 años. Incluye tu Academia LUMI y todas las herramientas.",
                  inputRing: "focus-visible:ring-sky-500",
                  buttonCls: "bg-sky-500 hover:bg-sky-400 text-white",
                  cta: "Entrar como distribuidor LUMI",
                },
              };
              const ui = PIN_UI[pinMode] || PIN_UI.standard;
              return (
              <form onSubmit={redeemPin} className="space-y-3">
                <div className="flex items-center gap-2">
                  <ui.Icon size={16} className={ui.iconCls} />
                  <Label className="text-zinc-300 text-sm font-semibold">{ui.label}</Label>
                </div>
                <p className="text-xs text-zinc-500">{ui.desc}</p>
                <Input
                  data-testid="pin-code-input"
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  required
                  className={`bg-black border-white/15 rounded-xl py-6 font-mono tracking-wider ${ui.inputRing}`}
                />
                <Button
                  data-testid="redeem-pin-button"
                  type="submit"
                  disabled={redeeming}
                  className={`w-full font-bold rounded-xl py-6 ${ui.buttonCls}`}
                >
                  {redeeming ? <Loader2 className="animate-spin mr-2" size={18} /> : <ui.Icon size={16} className="mr-2" />}
                  {ui.cta}
                </Button>
                <button
                  type="button"
                  data-testid="pin-mode-back"
                  onClick={() => { setPinMode(null); setPinCode(""); }}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-300"
                >
                  ← Elegir otro tipo de PIN
                </button>
              </form>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
